import React from 'react';
import Header from '@/components/Header';
import ManufacturerLogos from '@/components/ManufacturerLogos';
import { Card, CardContent } from '@/components/ui/card';
import { Plane, Shield, Globe, Users } from 'lucide-react';
import Footer from '@/components/sections/Footer';

export default function AboutPage() {
  return (
    <div className="min-h-screen">
      <Header />
      <ManufacturerLogos />

      {/* Hero */}
      <section className="bg-gradient-to-b from-muted/50 to-background py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              About <span className="text-gradient bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">UAVs For Sale</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              The world's leading marketplace connecting industrial drone buyers and sellers globally.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center">Our Mission</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              UAVs For Sale was founded to solve a critical challenge in the industrial drone industry: connecting professional buyers with verified sellers in a trusted, efficient marketplace.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Whether you're a construction company needing thermal inspection drones, an agricultural operation seeking spraying UAVs, or a cinematographer looking for professional aerial platforms, we provide the most comprehensive selection of industrial drones from verified vendors worldwide.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our broker team ensures every transaction is secure, every listing is verified, and every buyer finds exactly what they need.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center">Why Choose Us</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Verified Vendors</h3>
                  <p className="text-muted-foreground">
                    Every seller undergoes rigorous verification to ensure quality and reliability.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center p-6">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <Globe className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Global Reach</h3>
                  <p className="text-muted-foreground">
                    Access industrial drones from verified vendors in over 100 countries worldwide.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center p-6">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-secondary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Expert Support</h3>
                  <p className="text-muted-foreground">
                    Our broker team has deep aerospace and industrial expertise to guide you.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center p-6">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Plane className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Industry Leading</h3>
                  <p className="text-muted-foreground">
                    The largest selection of industrial UAVs across all categories and applications.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-primary mb-2">2,500+</div>
                <div className="text-muted-foreground font-medium">Active Listings</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-accent mb-2">100+</div>
                <div className="text-muted-foreground font-medium">Countries Served</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-secondary mb-2">500+</div>
                <div className="text-muted-foreground font-medium">Verified Vendors</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Get in Touch</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Have questions about buying or selling industrial drones? Our team is here to help.
            </p>
            <div className="space-y-3 text-muted-foreground">
              <p><strong>Email:</strong> info@uavsforsale.com</p>
              <p><strong>Phone:</strong> +1 (555) 123-4567</p>
              <p><strong>Location:</strong> San Francisco, CA</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}